import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import Galaxy3D from "@/components/Galaxy3D";
import SolarSystem3D, { SOLAR_PLANETS } from "@/components/SolarSystem3D";
import SpaceNoiseListener from "@/components/SpaceNoiseListener";
import api, { formatErr } from "@/lib/api";
import { useLang } from "@/i18n";
import { Crosshair, X, Compass, Telescope, Rocket, ChevronRight, Sun, Hourglass } from "lucide-react";

const LaunchOverlay = ({ destinationName, lang }) => (
  <div
    className="fixed inset-0 z-[200] flex items-center justify-center backdrop-blur-md"
    style={{ background: "radial-gradient(ellipse at center, rgba(3,4,11,0.85) 0%, rgba(3,4,11,0.98) 100%)" }}
    data-testid="launch-overlay"
  >
    <div className="text-center px-6">
      <div className="relative w-32 h-32 mx-auto mb-8">
        <div
          className="absolute inset-0 rounded-full border-2 border-[#00D4FF]/30"
          style={{ animation: "launchPulse 2.6s ease-in-out infinite" }}
        />
        <div
          className="absolute inset-2 rounded-full border border-[#62FFB0]/40"
          style={{ animation: "launchPulse 2.6s ease-in-out infinite 0.3s" }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Hourglass
            size={56}
            className="text-[#00D4FF]"
            style={{ animation: "hourglassFlip 1.8s ease-in-out infinite", filter: "drop-shadow(0 0 18px rgba(0,212,255,0.65))" }}
          />
        </div>
      </div>
      <div className="label-overline mb-3 text-[#00D4FF]">
        {lang === "pt" ? "Inicializando Transmissão" : "Initializing Transmission"}
      </div>
      <div className="font-display font-black text-2xl lg:text-3xl tracking-tighter">
        {lang === "pt" ? "Bloqueando alvo:" : "Locking target:"}{" "}
        <span className="text-[#62FFB0]">{destinationName}</span>
      </div>
      <div className="font-mono-data text-[10px] tracking-[0.22em] uppercase text-white/55 mt-4 max-w-md mx-auto leading-relaxed">
        {lang === "pt"
          ? "Sincronizando relay Amazon Kuiper · Gerando narrativa de missão · Assinando hash SHA-512"
          : "Synchronizing Amazon Kuiper relay · Generating mission narrative · Signing SHA-512 hash"}
      </div>
      <div className="mt-6 flex justify-center gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]"
            style={{ animation: `dotBlink 1.4s ease-in-out infinite ${i * 0.15}s` }}
          />
        ))}
      </div>
    </div>
    <style>{`
      @keyframes hourglassFlip {
        0%, 45% { transform: rotate(0deg); }
        50%, 95% { transform: rotate(180deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes launchPulse {
        0%, 100% { transform: scale(1); opacity: 0.6; }
        50% { transform: scale(1.18); opacity: 1; }
      }
      @keyframes dotBlink {
        0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1.2); }
      }
    `}</style>
  </div>
);

export default function GalacticNav() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fromCapsule = params.get("capsule");
  const [mode, setMode] = useState("solar");
  const [planets, setPlanets] = useState([]);
  const [solarDest, setSolarDest] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedKind, setSelectedKind] = useState(null); // 'exoplanet' | 'solar'
  const [hoveredId, setHoveredId] = useState(null);
  const [solarSelected, setSolarSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exoplanets`).then((r) => r.json()).then(setPlanets).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/solar-destinations`).then((r) => r.json()).then(setSolarDest).catch(() => {});
  }, []);

  const selected = useMemo(() => {
    if (selectedKind === "exoplanet") return planets.find((p) => p.id === selectedId);
    if (selectedKind === "solar") return solarDest.find((p) => p.id === selectedId);
    return null;
  }, [planets, solarDest, selectedId, selectedKind]);

  const setAsDestination = async () => {
    if (!selected) return;
    if (!fromCapsule) {
      sessionStorage.setItem("s2s_destination", selected.id);
      nav("/upload");
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data: tx } = await api.post("/transmissions/start", {
        capsule_id: fromCapsule,
        destination_id: selected.id,
      });
      sessionStorage.removeItem("s2s_capsule_files");
      sessionStorage.removeItem("s2s_destination");
      nav(`/transmission/${tx.transmission_id}`);
    } catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };

  // (Unused after panel unification — kept for potential future debug)
  // const solarPlanet = SOLAR_PLANETS.find((p) => p.name === solarSelected);

  return (
    <AppShell>
      {busy && fromCapsule && (
        <LaunchOverlay destinationName={selected?.name || "—"} lang={lang} />
      )}
      <div className="relative" style={{ height: "calc(100vh - 100px)" }}>
        {mode === "solar" ? (
          <SolarSystem3D
            onSelect={(name) => {
              setSolarSelected(name);
              const body = solarDest.find((s) => s.name.toLowerCase().startsWith(name.toLowerCase()));
              if (body) { setSelectedId(body.id); setSelectedKind("solar"); }
            }}
            selected={solarSelected}
            className="absolute inset-0"
          />
        ) : (
          <Galaxy3D
            planets={planets}
            onSelect={(p) => { setSelectedId(p.id); setSelectedKind("exoplanet"); }}
            selectedId={selectedKind === "exoplanet" ? selectedId : null}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            className="absolute inset-0"
          />
        )}

        {/* HUD top */}
        <div className="absolute top-0 left-0 right-0 p-4 lg:p-6 flex items-start justify-between pointer-events-none">
          <div className="pointer-events-auto holo-panel px-4 py-3 max-w-md">
            <div className="label-overline flex items-center gap-2">
              <Compass size={12} /> {mode === "solar"
                ? (lang === "pt" ? "Sistema Solar" : "Solar System")
                : (lang === "pt" ? "Galáxia · Via Láctea" : "Galaxy · Milky Way")}
            </div>
            <div className="font-display font-black text-xl tracking-tighter mt-1" data-testid="galaxy-title">
              {mode === "solar"
                ? (lang === "pt" ? "Centro Heliosférico" : "Heliospheric Center")
                : (lang === "pt" ? "Centro de Navegação Galáctica" : "Galactic Navigation Center")}
            </div>
            <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-2">
              {mode === "solar"
                ? `${SOLAR_PLANETS.length} ${lang === "pt" ? "planetas do sistema" : "system planets"}`
                : `${planets.length} ${lang === "pt" ? "destinos catalogados" : "habitable targets"}`}
            </div>
          </div>

          {/* Mode toggle */}
          <div className="pointer-events-auto holo-panel p-1 flex items-center gap-1" data-testid="nav-mode-toggle">
            <button
              onClick={() => setMode("solar")}
              data-testid="mode-solar"
              className={`px-3 py-2 font-mono-data text-[11px] tracking-[0.2em] uppercase flex items-center gap-2 transition-all ${
                mode === "solar" ? "bg-[#00D4FF] text-[#03040B]" : "text-white/65 hover:text-white"
              }`}
            >
              <Sun size={12} /> {lang === "pt" ? "Solar" : "Solar"}
            </button>
            <button
              onClick={() => setMode("galaxy")}
              data-testid="mode-galaxy"
              className={`px-3 py-2 font-mono-data text-[11px] tracking-[0.2em] uppercase flex items-center gap-2 transition-all ${
                mode === "galaxy" ? "bg-[#00D4FF] text-[#03040B]" : "text-white/65 hover:text-white"
              }`}
            >
              <Telescope size={12} /> {lang === "pt" ? "Galáxia" : "Galaxy"}
            </button>
          </div>
        </div>

        {/* Bottom catalogue strip */}
        <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 pointer-events-none">
          <div className="pointer-events-auto holo-panel overflow-hidden">
            <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
              <Telescope size={12} className="text-[#00D4FF]" />
              <div className="label-overline">
                {mode === "solar"
                  ? (lang === "pt" ? "Destinos do Sistema Solar" : "Solar System Destinations")
                  : (lang === "pt" ? "Catálogo de Exoplanetas" : "Exoplanet Catalogue")}
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto p-3" data-testid={mode === "solar" ? "solar-strip" : "planet-strip"}>
              {(mode === "solar" ? solarDest : planets).map((p) => {
                const isSolar = mode === "solar";
                const dist = isSolar
                  ? (p.distance_au < 1 ? `${(p.distance_km / 1000).toFixed(0)} k km` : `${p.distance_au.toFixed(2)} AU`)
                  : `${p.distance_ly.toFixed(p.distance_ly < 100 ? 2 : 0)} ly`;
                return (
                  <button
                    key={p.id}
                    data-testid={`${isSolar ? "solar" : "planet"}-card-${p.id}`}
                    onClick={() => { setSelectedId(p.id); setSelectedKind(isSolar ? "solar" : "exoplanet"); }}
                    className={`flex-shrink-0 text-left p-3 border min-w-[200px] transition-all ${
                      selectedId === p.id
                        ? "border-[#00D4FF] bg-[#00D4FF]/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="font-mono-data text-[10px] tracking-[0.18em] uppercase text-[#00D4FF]">
                      {dist}
                    </div>
                    <div className="font-display font-bold text-sm mt-1 tracking-tight">{p.name}</div>
                    <div className="font-mono-data text-[10px] text-white/55 mt-1 truncate">
                      {isSolar ? (p.type || "—") : p.constellation}
                    </div>
                    <div className="mt-2 h-1 bg-white/5 relative overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full"
                        style={{
                          width: `${(p.habitability || 0) * 100}%`,
                          background: (p.habitability || 0) > 0.8 ? "#62FFB0" : "#00D4FF",
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Unified destination info panel for both solar + galaxy */}
        {selected && (
          <div
            className="absolute top-1/2 -translate-y-1/2 right-4 lg:right-8 w-[92%] max-w-md pointer-events-auto"
            data-testid="planet-info-panel"
          >
            <div className="holo-panel relative">
              <button
                onClick={() => { setSelectedId(null); setSelectedKind(null); }}
                className="absolute top-3 right-3 text-white/60 hover:text-white"
                data-testid="planet-info-close"
              >
                <X size={16} />
              </button>
              <div className="p-5 border-b border-white/5">
                <div className="label-overline flex items-center gap-2">
                  <Crosshair size={12} /> {lang === "pt" ? "Alvo Selecionado" : "Target Locked"}
                </div>
                <div className="font-display font-black text-2xl tracking-tighter mt-2" data-testid="planet-info-name">
                  {selected.name}
                </div>
                <div className="font-mono-data text-[11px] tracking-[0.16em] uppercase text-white/55 mt-1">
                  {selectedKind === "solar"
                    ? `${selected.type || "—"} · ${selected.parent || "—"}`
                    : `${selected.constellation} · ${selected.star_type}`}
                </div>
              </div>
              <div className="p-5 space-y-3 max-h-[40vh] overflow-y-auto">
                <p className="text-white/75 text-sm leading-relaxed">{selected.description}</p>
                <div className="grid grid-cols-2 gap-2 mt-4 text-[12px]">
                  {(selectedKind === "exoplanet" ? [
                    [lang === "pt" ? "Distância" : "Distance", `${selected.distance_ly.toFixed(2)} ly`],
                    [lang === "pt" ? "Descoberta" : "Discovery", selected.discovery_year],
                    [lang === "pt" ? "Raio" : "Radius", `${selected.radius_earth.toFixed(2)} R⊕`],
                    [lang === "pt" ? "Massa" : "Mass", `${selected.mass_earth.toFixed(2)} M⊕`],
                    [lang === "pt" ? "Período" : "Orbital Period", `${selected.orbital_period_days} d`],
                    [lang === "pt" ? "Temp. Superfície" : "Surface Temp", `${selected.surface_temp_c}°C`],
                    ["RA (J2000)", `${selected.ra_deg.toFixed(2)}°`],
                    ["DEC (J2000)", `${selected.dec_deg.toFixed(2)}°`],
                  ] : [
                    [lang === "pt" ? "Distância (AU)" : "Distance (AU)", `${selected.distance_au.toFixed(3)} AU`],
                    [lang === "pt" ? "Distância (km)" : "Distance (km)", `${(selected.distance_km / 1e6).toFixed(2)} ×10⁶`],
                    [lang === "pt" ? "Tipo" : "Type", selected.type],
                    [lang === "pt" ? "Orbita" : "Parent", selected.parent],
                    [lang === "pt" ? "Raio" : "Radius", `${(selected.radius_km || 0).toLocaleString()} km`],
                    [lang === "pt" ? "Sinal (luz)" : "Signal (light)", selected.signal_light_seconds < 60
                      ? `${selected.signal_light_seconds.toFixed(2)} s`
                      : `${(selected.signal_light_seconds / 60).toFixed(1)} min`],
                  ]).map(([k, v]) => (
                    <div key={k}>
                      <div className="label-overline text-[10px]">{k}</div>
                      <div className="font-mono-data text-[#00D4FF] mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
                {selectedKind === "exoplanet" && (
                  <div className="pt-2">
                    <div className="label-overline text-[10px]">{lang === "pt" ? "Índice de Habitabilidade" : "Habitability Index"}</div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <div className="flex-1 h-1.5 bg-white/5 relative overflow-hidden">
                        <div
                          className="absolute left-0 top-0 h-full"
                          style={{
                            width: `${selected.habitability * 100}%`,
                            background: selected.habitability > 0.8 ? "#62FFB0" : "#00D4FF",
                          }}
                        />
                      </div>
                      <div className="font-mono-data text-sm text-[#00D4FF]">{(selected.habitability * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-5 border-t border-white/5">
                <SpaceNoiseListener
                  destination={{
                    id: selected.id,
                    name: selected.name,
                  }}
                  lang={lang}
                  compact
                />
              </div>
              <div className="p-5 border-t border-white/5">
                <button
                  onClick={setAsDestination}
                  disabled={busy}
                  data-testid="set-as-destination"
                  className="btn-primary-s2s w-full justify-center !text-[11px] disabled:opacity-50"
                >
                  <Rocket size={14} />
                  {fromCapsule
                    ? (lang === "pt" ? "INICIAR PREVIEW DA MISSÃO" : "START MISSION PREVIEW")
                    : (lang === "pt" ? "DEFINIR COMO DESTINO" : "SET AS DESTINATION")}
                  <ChevronRight size={14} />
                </button>
                {err && <div className="mt-2 text-[#FF3366] font-mono-data text-xs">{err}</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
